'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {
  StrapiMarketExpansion,
  StrapiMarketExpansionEvent
} from '@/types/strapi';
import {cloneDeep} from 'lodash';
import {useLocale, useTranslations} from 'next-intl';
import {useEffect, useRef, useState} from 'react';
import {observeFade} from '../helpers/observe-fade';
import {LazyImage} from '../tools/LazyImage';
import {Carousel, CarouselContent} from '../ui/carousel';
import ComingSoon from './ComingSoon';
import MediaPreview from './MediaPreview';

interface MilestoneItem
  extends StrapiMarketExpansion,
    StrapiMarketExpansionEvent {
  listID: string;
}
export default function MarketExpansion(Prop: {moduleData: any}) {
  const locale = useLocale();
  const [searchResults, setSearchResults] = useState<StrapiMarketExpansion[]>(
    []
  );
  const [filterResults, setFilterResults] = useState<MilestoneItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  const isLoadingRef = useRef(false);
  const t = useTranslations('MarketExpansion');
  const [refreshMedia, setRefreshMedia] = useState(0);

  const marketExpansionGroup = [
    {title: t('all'), slug: 'all'},
    ...Prop.moduleData
  ];

  const marketExpansionGroupMap = marketExpansionGroup.reduce(
    (acc, cur) => ({...acc, [cur.slug]: cur}),
    {}
  );

  const [marketExpansionGroups, setMarketExpansionGroups] = useState<{
    [key: string]: {
      title: string;
      slug: string;
      count: number;
    };
  }>({
    all: {title: t('all'), slug: 'all', count: 0}
  });

  const [marketExpansionFilter, setMarketExpansionFilter] = useState({
    filterGroup: 'all'
  });
  const getSearchResult = async () => {
    if (isLoadingRef.current) {
      return;
    }
    setIsLoading(true);
    isLoadingRef.current = true;
    const strapiOption = {
      sort: 'title:asc',
      pagination: {pageSize: 9999}
    };
    const postData = await getStrapiData(
      locale,
      'market-expansions',
      strapiOption
    );
    setSearchResults((prev) => {
      if (postData?.data) {
        postData?.data.map((d: StrapiMarketExpansion) => {
          d.events?.map((e) => {
            if (e.brands) {
              setMarketExpansionGroups((prev) => {
                const d = cloneDeep(prev);
                d.all.count += 1;
                if (e.brands && e.brands.length > 0) {
                  const brands = e.brands;
                  brands.forEach((brand) => {
                    if (!d[brand]) {
                      d[brand] = {
                        slug: brand,
                        title: marketExpansionGroupMap[brand].title,
                        count: 0
                      };
                    }
                    d[brand].count += 1;
                  });
                }
                return d;
              });
            }
          });
        });
        return [...prev, ...postData?.data];
      }
      return [...prev, ...[]];
    });

    setIsLoading(false);
    isLoadingRef.current = false;
  };

  useEffect(() => {
    setTimeout(getSearchResult);
  }, []);

  useEffect(() => {
    setRefreshMedia((prev) => prev + 1);
  }, [filterResults]);

  const paddingMap = ['', 'md:pt-[54px]'];

  const chunkArray = (array: any[], size: number) => {
    if (size <= 0) {
      throw new Error('Chunk size must be a positive integer.');
    }
    const chunks: MilestoneItem[][] = [];
    for (let i = 0; i < size; i++) {
      chunks.push([]);
    }
    for (let i = 0; i < array.length; i++) {
      const chunkIdx: number = i % size;
      chunks[chunkIdx].push(array[i]);
    }
    return chunks;
  };

  useEffect(() => {
    setFilterResults([]);
    setFilterResults(
      searchResults
        .flatMap((i, index) =>
          (i.events ?? [])
            .filter((e: any) => e.content && e.image)
            .map((e, jndex) => {
              const k = {...i};
              delete k.events;
              return {
                ...k,
                ...e,
                listID: `${index}-${jndex}`
              };
            })
        )
        .filter((result) => {
          if (marketExpansionFilter.filterGroup === 'all') {
            return true;
          }
          return result.brands?.includes(marketExpansionFilter.filterGroup);
        })
    );
    setTimeout(() => {
      observeFade();
    }, 100);
  }, [searchResults, marketExpansionFilter]);

  const marketItem = (result: MilestoneItem, index: number) => {
    return (
      <div
        className={cn(
          'flex flex-col xl:flex-row justify-center items-center w-full',
          'gap-x-[32px] gap-y-[16px] max-w-screen-sm  '
        )}
        data-fade=""
        data-fade-threshold="0.8"
        key={index}
      >
        {result.image && (
          <div className="w-full xl:w-1/2">
            <LazyImage
              src={result.image}
              alt={result.title}
              className={cn('aspect-[4/3] object-cover cursor-pointer')}
              data-media={result.image}
            />
          </div>
        )}
        <div
          className={cn(
            'flex flex-[1_1_0] items-center gap-x-[16px] gap-y-[20px] w-full xl:w-1/2 ',
            'md:items-start md:flex-col leading-[24px]'
          )}
        >
          <div className={cn('text-[#000000] font-[700] text-[28px]')}>
            {result.title}
          </div>
          <div
            className={cn('text-[#363636] text-base')}
            dangerouslySetInnerHTML={{__html: result.content}}
          ></div>
        </div>
      </div>
    );
  };
  const marketFilterButton = () => {
    return (
      <>
        {Object.values(marketExpansionGroups)
          .sort((a, b) => {
            return b.count - a.count;
          })
          .map((a, i) => (
            <button
              key={i}
              className={cn(
                'text-[#000000] md:text-[15px] text-[14px] leading-[25px] ubuntu',
                'md:whitespace-nowrap md:rounded-full md:px-8 md:py-3 py-4 text-left md:text-center border-b border-[#D9D9D9] md:border-0',
                marketExpansionFilter.filterGroup === a.slug
                  ? 'md:bg-[#E22219] text-[#E22219] md:text-white font-[700]'
                  : 'md:bg-[#FCEFEC] md:hover:bg-[#FFD0CE] font-[400]'
              )}
              onClick={() => {
                setMarketExpansionFilter({filterGroup: a.slug});
                setOpenMobileMenu(false);
              }}
            >
              {a.title}
            </button>
          ))}
      </>
    );
  };

  return (
    <div
      className={cn(
        'px-4 md:px-6 text-[18px] lg:max-w-[900px] xl:max-w-[1280px] mx-auto px-4'
      )}
    >
      <MediaPreview refresh={refreshMedia} />
      {false && (
        <div className="">
          <div className="relative md:hidden">
            <div
              className="md:hidden mx-auto max-w-[280px] flex items-center text-[#666666] gap-4 border-b border-[#FCEFEC] py-3"
              onClick={() => setOpenMobileMenu(!openMobileMenu)}
            >
              <span
                className={cn(
                  'material-symbols-rounded text-[24px] font-[300]'
                )}
              >
                home
              </span>
              {marketExpansionGroup
                .filter((e) => marketExpansionFilter.filterGroup === e.slug)
                .map((e: any) => (
                  <div
                    key={e.slug}
                    className={cn(
                      'grow text-[14px] leading-[20px] font-[300] ubuntu'
                    )}
                  >
                    {e.slug == 'all' ? t('allUnit') : e.title}
                  </div>
                ))}
              <span className="material-symbols-rounded text-[20px] text-[#E22219]">
                keyboard_arrow_down
              </span>
            </div>
            <div
              className={cn(
                !openMobileMenu && 'hidden',
                openMobileMenu && [
                  'absolute flex left-1/2 top-full translate-y-3 -translate-x-1/2 z-5',
                  'bg-white overflow-auto max-h-[290px] w-full',
                  'px-6 rounded-xl shadow-[0px_2px_8px_0px_#0000001A]'
                ]
              )}
            >
              <div className={cn('flex flex-col w-full mx-auto hidden')}>
                {marketFilterButton()}
              </div>
            </div>
          </div>
          <Carousel opts={{dragFree: true}} className={cn('hidden md:block')}>
            <CarouselContent className="ml-0 gap-4">
              {marketFilterButton()}
            </CarouselContent>
          </Carousel>
        </div>
      )}
      {!isLoading && filterResults.length === 0 && <ComingSoon />}

      {filterResults.length > 0 && (
        <>
          <div
            className={cn(
              'hidden mt-[100px] md:flex gap-[60px] lg:max-w-[900px] xl:max-w-[1280px] mx-auto px-4'
            )}
          >
            {chunkArray(filterResults, 2).map((chunk, index) => (
              <div
                key={index}
                className={cn(
                  paddingMap[index],
                  'flex flex-1 flex-col gap-[80px] fade-in'
                )}
              >
                {chunk?.map((result: MilestoneItem, index: number) =>
                  marketItem(result, index)
                )}
              </div>
            ))}
          </div>
          <div className={cn('md:hidden mt-[41px] flex flex-1 flex-col gap-8')}>
            {chunkArray(filterResults, 1).map((chunk, index) => (
              <div
                key={index}
                className={cn(
                  'flex flex-1 flex-col gap-8 fade-in items-center'
                )}
              >
                {chunk?.map((result: MilestoneItem, index: number) =>
                  marketItem(result, index)
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {isLoading && (
        <div className="text-center w-full">
          <div className={cn(filterResults.length <= 0 && 'pt-[50px]')}>
            <span
              className={cn(
                'material-symbols thin animate-spin  text-[#eee]',
                filterResults.length > 0 ? 'text-[30px]' : 'text-[100px]'
              )}
            >
              progress_activity
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
